import { IDataTableColumn, useGridState, useSearchApi, FieldSet, ButtonContainer } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { SubActivityJudgeApi, SubActivityJudgeGridDto, SubActivityJudgeSearch } from "../../../../../api";
import { ERoutes } from "../../../../../ERoutes";
import { CustomDataTable, AddButton, DeleteButton, ExportButton } from "../../../../../components";
import { useGlobalData } from "../../../../../contexts";
import { useApiService, useTl, useDeleteApi } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { Intent, Dialog, Colors, Classes, ButtonGroup } from "@blueprintjs/core";
import { PersonSubActivityJudgePanelStack } from "./PersonSubActivityJudgePanelStack";

export interface IPersonSubActivityJudgePageProps {
  personId: number;
  editMode: boolean;
}

export const PersonSubActivityJudgePage: React.FunctionComponent<IPersonSubActivityJudgePageProps> = ({
  personId,
  editMode,
}) => {
  const api = useApiService(SubActivityJudgeApi);
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();
  const [sObjState, setsobjState] = React.useState<any>();

  const [dialogOpened, setDialogOpened] = React.useState(false);

  const tableState = useGridState({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [5],
    pageSize: 5,
    sortKeys: { lastName: "ASC" },
  });

  const { totalCount } = tableState;

  const searchFunction = React.useCallback(
    (sObj: SubActivityJudgeSearch) => {
      const sobj = SubActivityJudgeSearch.fromJS({
        ...sObj,
        personId: personId,
        languageId: currentLanguage.languageId,
      });
      setsobjState(sobj);
      return api.baseSearch(sobj);
    },
    [api, currentLanguage.languageId, personId]
  );

  const { search, loading } = useSearchApi({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const { deleteItem } = useDeleteApi(
    React.useMemo(
      () => ({
        deleteApiFn: (data: number) => api.deleteSubActivityJudge(data),
        onDeletedFn: () => search(),
      }),
      [api, search]
    )
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        hidden: !editMode,
        render: (row: SubActivityJudgeGridDto) => (
          <ButtonContainer>
            <DeleteButton minimal={true} onDelete={() => deleteItem(row.subActivityJudgeId)} />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.Activity),
        fieldName: "activity",
        render: (row: SubActivityJudgeGridDto) => (
          <Link target="_blank" to={`${ERoutes.activity}/${row.activityId}/detail/view`}>
            {row.activity}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Name),
        fieldName: "name",
        render: (row: SubActivityJudgeGridDto) => (
          <Link target="_blank" to={`${ERoutes.subActivity}/${row.subActivityId}/detail/view`}>
            {row.name}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.JudgeType),
        fieldName: "judgeType",
      },
      {
        header: () => t(ETLCodes.JudgeDateNomination),
        fieldName: "nominationDate",
      },
      {
        header: () => t(ETLCodes.JudgeDateInternship),
        fieldName: "internshipDate",
      },
      {
        header: () => t(ETLCodes.JudgeDateExcludedFrom),
        fieldName: "excludedFrom",
      },
      {
        header: () => t(ETLCodes.JudgeDateExcludedTo),
        fieldName: "excludedTo",
      },
    ],
    [deleteItem, editMode, t]
  );

  const addItemFunction = React.useCallback(() => {
    setDialogOpened(true);
  }, []);
  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);

  return (
    <FieldSet
      title={t(ETLCodes.SubActivities, { totalCount: totalCount })}
      rightElement={
        <ButtonGroup>
          <AddButton
            onClick={(e) => {
              e.stopPropagation();
              addItemFunction();
            }}
            text={t(ETLCodes.General_Add)}
            intent={Intent.PRIMARY}
          />
          <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sObjState} />
        </ButtonGroup>
      }
    >
      <CustomDataTable tableState={tableState} loading={loading} columns={columns} />

      <Dialog
        title={t(ETLCodes.SubActivities)}
        isOpen={dialogOpened}
        onClose={() => setDialogOpened(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        style={{ width: 1000, height: 750, background: Colors.WHITE }}
        className={Classes.DIALOG}
      >
        <div className={Classes.DIALOG_BODY} style={{ height: 200 }}>
          <PersonSubActivityJudgePanelStack
            personId={personId}
            onSaved={() => {
              setDialogOpened(false);
              search();
            }}
          />
        </div>
      </Dialog>
    </FieldSet>
  );
};
